/**
 * useProfileUpdater Hook
 * Provides functionality to send user profile updates to Pub/Sub via API
 */
import { useContext } from 'react';
import GlobalContext from 'hooks/contexts/GlobalContext';
import BasicFormWizardContext from 'hooks/contexts/BasicFormWizardContext';
import { FIELD_NAMES, QUERY_PARAMS } from 'consts';
import { getFieldValue } from 'utils/formValuesUtils';
import { ErrorPriority, FormValues } from 'types';
import { getAllCookies, getStoredQueryParams } from 'utils/analyticsHelpers';
import { LogError } from 'utils/logging/LogError';
import { LogWarning } from 'utils/logging';
import { hashPii } from 'utils/logging/hash';
import {
  UpdateUserProfileRequest,
  EventType,
} from './UpdateUserProfileRequestType';
import { getUserProfileManager } from './UserProfile';

const { SHARED_SESSION_ID } = QUERY_PARAMS;
const {
  EMAIL,
  PRIMARY_PHONE,
  FIRST_NAME,
  LAST_NAME,
  ZIP,
  BEST_MATCHING_PROGRAM,
  CITY,
  STATE,
} = FIELD_NAMES;

enum ConsentType {
  TARGETED_ADVERTISING = 'targeted_advertising',
  ESSENTIAL_SERVICES = 'essential_services',
}

interface SendProfileUpdateParams {
  eventType: EventType;
  possibleFormValues?: FormValues;
}

function findConsentInDataLayer(
  consentType: ConsentType,
  eventType: EventType
): 'granted' | 'denied' {
  try {
    if (!window.dataLayer) {
      return 'granted';
    }

    const consent = [...(window.dataLayer || [])]
      ?.reverse()
      .find(
        (event: { [key: string]: unknown }) =>
          typeof event[consentType] !== 'undefined'
      )?.[consentType];

    if (typeof consent === 'boolean') {
      return consent ? 'granted' : 'denied';
    }

    if (eventType !== EventType.PAGE_VIEW) {
      LogWarning('Consent value is not a boolean', {
        description: `Consent value is not a boolean as ${eventType}: ${consent}`,
        priority: ErrorPriority.ONE,
      });
    }
    return 'granted';
  } catch (error) {
    LogError('Error finding consent in data layer', {
      description: error instanceof Error ? error.message : 'Unknown error',
      priority: ErrorPriority.ONE,
    });
    return 'granted';
  }
}

function getConsentValues(eventType: EventType): string {
  try {
    return JSON.stringify({
      targeted_advertising: findConsentInDataLayer(
        ConsentType.TARGETED_ADVERTISING,
        eventType
      ),
      essential_services: findConsentInDataLayer(
        ConsentType.ESSENTIAL_SERVICES,
        eventType
      ),
    });
  } catch (error) {
    LogError('Error getting consent values', {
      description: error instanceof Error ? error.message : 'Unknown error',
      priority: ErrorPriority.ONE,
    });
    return '';
  }
}

/**
 * Custom hook to send profile updates to Pub/Sub
 */
export function useProfileUpdater(): {
  sendProfileUpdate: (params: SendProfileUpdateParams) => Promise<void>;
  setPageViewIdTaxonomyMap: (resetIfNoneSet?: boolean) => void;
  addConsentListener: (listener: () => void) => void;
  removeConsentListener: (listener: () => void) => void;
} {
  const globalContext = useContext(GlobalContext);
  const formContext = useContext(BasicFormWizardContext);
  const userProfileManager = getUserProfileManager();

  async function sendProfileUpdate(
    params: SendProfileUpdateParams
  ): Promise<void> {
    try {
      // Extract data from contexts
      const { eventType, possibleFormValues } = params;
      const { formValues } = formContext;
      const { siteMeta, sessionInfo } = globalContext;
      const { queryParamMap } = getStoredQueryParams() as unknown as {
        queryParamMap: Record<string, string>;
      };
      const { map: cookiesMap } = getAllCookies() as unknown as {
        map: Record<string, string>;
      };

      const _formValues = possibleFormValues || formValues;
      const _userIds = sessionInfo;

      // Get user tracking IDs
      const sessionId = _userIds?.userSessionId || '';
      const matchId = queryParamMap[SHARED_SESSION_ID] || sessionId;
      const pageViewId = _userIds?.pageViewId || '';

      // Get ads platform data from userData or query params
      const gclid = (queryParamMap.gclid as string) || '';
      const msclkid = (queryParamMap.msclkid as string) || '';
      const fbi = (cookiesMap._fbi as string) || '';
      const fbc = (cookiesMap._fbc as string) || '';
      const fbp = (cookiesMap._fbp as string) || '';

      // Get program of interest GUID
      const programGuid = getFieldValue(_formValues, BEST_MATCHING_PROGRAM);

      // Build the request payload
      const payload: UpdateUserProfileRequest = {
        eventInfo: {
          eventType,
          eventTimestamp: new Date().toISOString(),
        },
        pageData: {
          schoolCode: siteMeta?.schoolCode || '',
          pageTaxonomyValues:
            userProfileManager.getPageViewIdTaxonomyMap(pageViewId),
        },
        userIds: {
          matchId,
          sessionId,
          pageViewId,
        },
        adsPlatformData: {
          gclid,
          msclkid,
          fbi,
          fbc,
          fbp,
          consentValues: getConsentValues(eventType),
        },
        userProfile: {
          hashedEmail: hashPii(getFieldValue(_formValues, EMAIL), 'email'),
          hashedPhone: hashPii(
            getFieldValue(_formValues, PRIMARY_PHONE),
            'phone'
          ),
          hashedFirstName: hashPii(
            getFieldValue(_formValues, FIRST_NAME),
            'first_name'
          ),
          hashedLastName: hashPii(
            getFieldValue(_formValues, LAST_NAME),
            'last_name'
          ),
          hashedZipCode: hashPii(getFieldValue(_formValues, ZIP), 'zip_code'),
          programOfInterestGuid: programGuid,
          userAgent: navigator.userAgent,
          hashedCity: hashPii(getFieldValue(_formValues, CITY), 'city'),
          hashedState: hashPii(getFieldValue(_formValues, STATE), 'state'),
        },
      };

      // Make API call to uup endpoint
      userProfileManager.sendProfileUpdates(payload);
    } catch (error) {
      if (params.eventType !== EventType.PAGE_VIEW) {
        LogError('Error sending profile update', {
          description:
            error instanceof Error
              ? `${params.eventType} - ${error.message}`
              : 'Unknown error',
          priority: ErrorPriority.ONE,
        });
      }
    }
  }

  return {
    sendProfileUpdate,
    setPageViewIdTaxonomyMap: (resetIfNoneSet: boolean = false) =>
      userProfileManager.setPageViewIdTaxonomyMap(
        globalContext,
        resetIfNoneSet
      ),
    addConsentListener: (listener: () => void) =>
      userProfileManager.addConsentListener(listener),
    removeConsentListener: (listener: () => void) =>
      userProfileManager.removeConsentListener(listener),
  };
}
